import React, { FC, ReactNode, forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Card, Container, Flex, Heading, Icon, Select, Text, Notification } from "@telia/teddy";
import NexusLogoSymbol from "../../components/atoms/NexusLogoSymbol/NexusLogoSymbol";
import { useSelector, useDispatch } from "react-redux";
import { Outlet } from "react-router-dom";
import ExistingCustomerStep from "./ExistingCustomerStep";
import DevModeStep from "./DevModeStep";
import { FORM_ID } from "../../../../../constants";
import "./NewOrder.less";
import { RootState } from "../../../../../models";
import { Agent, LegalOwnerIndividual } from "../../../../../models/state";
import * as properNameCase from "../../../../../shared/properNameCase";
import { isTypeLoading } from "../../../loading/state/selectors";
import { useSelectedRetailer } from "../../hooks/useSelectedRetailer";
import { setCustomerTypeAndAgent, resetCustomerIdentification } from "../../state/actions";
import { RETAILER_LOOKUP_CUSTOMER_BY_PHONE_NUMBER } from "../../state/actionTypes";
import { getCustomerLegalOwnerIndividual } from "../../state/selectors/customerSelectors";
import { getSalesAgent, getSalesAgents } from "../../state/selectors/selectors";
import PAGES from "../../../../../constants/pages";
import TEXT from "../../text-consts";
import ImportantNewsBanner from "../../components/molecules/ImportantNewsBanner/ImportantNewsBanner";
import SupportLinks from "../../components/molecules/SupportLinks/SupportLinks";
import NotificationBell from "../../components/molecules/NotificationBell/NotificationBell";

type RetailerCustomerType = "new" | "existing" | "prepaid" | "continue" | "dev";

const CustomerTypeCard = forwardRef<
    HTMLDivElement,
    {
        title: string;
        children?: ReactNode;
        isLoading: boolean;
        disabled: boolean;
        onSelect: () => void;
    }
>(({ title, children, isLoading, disabled, onSelect }, ref) => (
    <Card
        ref={ref}
        layout="navigation-horizontal-small-centered"
        bordered
        variant="white"
        className="retailer-new-order__option"
    >
        <Card.Heading as="h4" variant="subsection-100">
            {title}
        </Card.Heading>
        {children && <Card.Content>{children}</Card.Content>}
        <Card.Button
            variant="secondary"
            aria-label={`Velg: ${title}`}
            iconOnly
            loading={isLoading}
            disabled={disabled}
            onClick={onSelect}
        >
            <Icon name="arrow-right" aria-hidden="true" focusable="false" />
        </Card.Button>
    </Card>
));
CustomerTypeCard.displayName = "CustomerTypeCard";

const ChooseCustomerTypeStep: FC<{
    customerType: RetailerCustomerType;
    showDevModeOption: boolean;
    legalOwner: Partial<LegalOwnerIndividual> | null;
    isLoading: boolean;
    onSelect: (type: RetailerCustomerType) => void;
}> = ({ legalOwner, customerType, showDevModeOption, isLoading, onSelect }) => {
    const firstOptionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        firstOptionRef.current?.focus();
    }, []);

    const cardProps = (type: RetailerCustomerType) => ({
        isLoading: isLoading && customerType === type,
        disabled: isLoading,
        onSelect: () => onSelect(type),
    });

    return (
        <Flex direction="column" gap="200">
            <CustomerTypeCard ref={firstOptionRef} title="Ny kunde" {...cardProps("new")} />
            <CustomerTypeCard title="Eksisterende kunde" {...cardProps("existing")} />
            <CustomerTypeCard title="Nytt kontantkort" {...cardProps("prepaid")} />
            {legalOwner && legalOwner.firstName && (
                <CustomerTypeCard title="Eller fortsette ordre for" {...cardProps("continue")}>
                    <Flex align="center" gap="100">
                        <Icon name="end-user" aria-hidden="true" focusable="false" />
                        <Text as="span" data-di-mask>
                            {properNameCase.convert(legalOwner.firstName)} {properNameCase.convert(legalOwner.lastName)}
                        </Text>
                    </Flex>
                </CustomerTypeCard>
            )}
            {showDevModeOption && <CustomerTypeCard title="Dev mode (skip identification)" {...cardProps("dev")} />}
        </Flex>
    );
};

const SelectShopAgent: FC<{
    agents: Agent[];
    selectedAgent: Agent | null;
    setSelectedAgent: (agent: Agent) => void;
}> = ({ agents, selectedAgent, setSelectedAgent }) => {
    const handleAgentSelect = (agentId?: string) => {
        const agent = agents.find((a) => `${a.id}` === `${agentId}`);
        if (agent) {
            setSelectedAgent(agent);
        }
    };
    const sortedAgents = useMemo(
        () =>
            [...agents].sort((a, b) =>
                `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, "no-NB"),
            ),
        [agents],
    );

    return (
        <Box mb="200">
            <Heading as="h3" variant="subsection-100" mb="100">
                Velg selger
            </Heading>
            <Select
                name="agent"
                options={sortedAgents.map((agent) => `${agent.id}`)}
                value={`${selectedAgent?.id || ""}`}
                className="retailer-new-order__select"
                onValueChange={(value: string) => {
                    handleAgentSelect(value);
                    return value;
                }}
            >
                <Select.Trigger className="retailer-new-order__select-trigger">
                    <Select.Value placeholder="Velge..." />
                </Select.Trigger>
                <Select.Portal>
                    <Select.Content className="retailer-new-order__select-content">
                        <Select.Viewport>
                            {sortedAgents.map((agent) => (
                                <Select.Item key={agent.id} value={`${agent.id}`}>
                                    <span data-di-mask>
                                        {agent.firstName} {agent.lastName}
                                    </span>
                                </Select.Item>
                            ))}
                        </Select.Viewport>
                    </Select.Content>
                </Select.Portal>
            </Select>
        </Box>
    );
};

const NewOrderPage: FC = () => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isDevMode = useSelector((state: RootState) => !!state.config?.devMode);
    const isAtEnvironment = useSelector((state: RootState) => state.config?.environment === "at");
    const showDevModeOption = isDevMode || isAtEnvironment;
    const salesAgents = useSelector((state: RootState) => getSalesAgents(state));
    const salesAgent = useSelector((state: RootState) => getSalesAgent(state));
    const legalOwner = useSelector((state: RootState) => getCustomerLegalOwnerIndividual(state));
    const isLoadingCustomerData = useSelector((state: RootState) =>
        isTypeLoading(state, RETAILER_LOOKUP_CUSTOMER_BY_PHONE_NUMBER),
    );
    const [stepIndex, setStepIndex] = useState(0);
    const [customerType, setCustomerType] = useState<RetailerCustomerType>("new");
    const [selectedAgent, setSelectedAgent] = useState(
        salesAgents.find((a) => a.email === salesAgent?.email) || salesAgents[0] || null,
    );
    const [hasPhoneError, setHasPhoneError] = useState(false);
    const { selectedRetailer } = useSelectedRetailer();

    const handleSelectCustomerType = async (type: RetailerCustomerType) => {
        setCustomerType(type);
        setError(null);

        if (type === "existing") {
            setStepIndex(1);
            return;
        }
        if (type === "dev") {
            return;
        }

        try {
            setIsLoading(true);
            if (type === "continue") {
                try {
                    await dispatch(resetCustomerIdentification());
                } catch (_) {
                    // Ignore errors - proceed even if reset fails
                }
                window.location.assign(PAGES.RETAILER.ORDER.CUSTOMER.URL);
                return;
            }

            const result = await dispatch(setCustomerTypeAndAgent(type, selectedAgent));
            if (result.success) {
                window.location.assign(PAGES.RETAILER.ORDER.CUSTOMER.URL);
            }
        } catch (err: any) {
            setError(TEXT.ERRORS.ORDER_TYPE_SELECTION_ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            className="retailer-new-order"
            style={{ backgroundImage: "url(/shop/assets/img-static/retailer/reseller-background.png)" }}
        >
            <Container className="retailer-new-order__container">
                <div className="retailer-new-order__banner">
                    <ImportantNewsBanner />
                </div>
                <Box className="retailer-new-order__card">
                    <Flex justify="between" align="center" className="retailer-new-order__logo">
                        <Flex align="center" gap="100">
                            <NexusLogoSymbol />
                            <Heading mb="100" as="h1" variant="title-300" className="retailer-new-order__logo-text">
                                Telia Nexus
                            </Heading>
                        </Flex>
                        <NotificationBell />
                    </Flex>
                    <Flex justify="between" align="center" mb="300" mt="200">
                        <Heading className="retailer-new-order__heading" as="h2" variant="title-200">
                            Ny ordre
                        </Heading>
                        <Button
                            onClick={() => {
                                window.location.assign("/nexus/butikker/");
                            }}
                            variant="text-underline"
                        >
                            {`Butikk: ${salesAgent?.dealerName || selectedRetailer?.dealerName}` || "Velg butikk"}
                        </Button>
                    </Flex>
                    {!!error && (
                        <Notification type="tile" variant="error" className="retailer-new-order__error">
                            <Notification.Icon />
                            <Notification.Text>{error}</Notification.Text>
                        </Notification>
                    )}
                    {stepIndex === 0 && (
                        <>
                            <SelectShopAgent
                                agents={salesAgents}
                                selectedAgent={selectedAgent}
                                setSelectedAgent={setSelectedAgent}
                            />
                            <Heading as="h3" variant="subsection-100" mb="100" mt="300">
                                Velg ordretype
                            </Heading>
                            <ChooseCustomerTypeStep
                                legalOwner={legalOwner}
                                showDevModeOption={showDevModeOption}
                                customerType={customerType}
                                isLoading={isLoading}
                                onSelect={handleSelectCustomerType}
                            />
                            {customerType === "dev" && <DevModeStep agent={selectedAgent} />}
                        </>
                    )}

                    {stepIndex === 1 && customerType === "existing" && (
                        <ExistingCustomerStep agent={selectedAgent} onHasError={setHasPhoneError} />
                    )}
                    {stepIndex === 1 && (
                        <Flex justify="between">
                            <Button
                                onClick={() => {
                                    setStepIndex(0);
                                }}
                                mt="300"
                                variant="text"
                            >
                                Tilbake
                            </Button>
                            <Button
                                mt="300"
                                type="submit"
                                form={FORM_ID.RETAILER_LOOKUP}
                                disabled={isLoadingCustomerData || hasPhoneError}
                                loading={isLoadingCustomerData}
                            >
                                Neste
                                <Icon name="arrow-right" aria-hidden="true" focusable="false" />
                            </Button>
                        </Flex>
                    )}
                    <SupportLinks />
                </Box>
            </Container>

            <Outlet />
        </div>
    );
};

export default NewOrderPage;
